import React from 'react'
import { useAuth } from '@/context/AuthContext'
import { Result, Card } from 'antd'
import { LockOutlined } from '@ant-design/icons'

interface PermissionGateProps {
  children: React.ReactNode
  /**
   * Feature name to check access for
   */
  featureId?: string
  /**
   * Permission type required (default: 'read')
   */
  permission?: 'read' | 'write' | 'delete'
  /**
   * Role names that can access this component
   */
  requiredRoles?: string[]
  /**
   * Custom permission checker function
   */
  customCheck?: (user: any) => boolean
  /**
   * What to render when access is denied
   */
  fallback?: React.ReactNode
  /**
   * Whether to show the children when user is loading
   */
  showWhileLoading?: boolean
}

export function PermissionGate({
  children,
  featureId,
  permission = 'read',
  requiredRoles = [],
  customCheck,
  fallback,
  showWhileLoading = false,
}: PermissionGateProps) {
  const { user, loading } = useAuth()

  // Show nothing or loading state while authentication is being checked
  if (loading) {
    return showWhileLoading ? <>{children}</> : null
  }

  // No user - deny access
  if (!user) {
    return fallback || null
  }

  // Custom permission check
  if (customCheck) {
    if (!customCheck(user)) {
      return fallback || <AccessDeniedFallback />
    }
    return <>{children}</>
  }

  // Check role requirements
  if (requiredRoles.length > 0) {
    const userRoles = user.user_roles?.map(ur => ur.role.name) || []
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role))
    
    if (!hasRequiredRole) {
      return fallback || <AccessDeniedFallback />
    }
  }

  // Check feature access
  if (featureId) {
    const hasFeatureAccess = user.feature_access?.some(fa => {
      if (fa.feature.id !== featureId) return false
      
      switch (permission) {
        case 'read':
          return fa.can_read
        case 'write':
          return fa.can_write
        case 'delete':
          return fa.can_delete
        default:
          return false
      }
    })

    if (!hasFeatureAccess) {
      return fallback || <AccessDeniedFallback />
    }
  }

  // If no specific checks are defined, allow access
  return <>{children}</>
}

// Default fallback component for access denied
function AccessDeniedFallback() {
  return (
    <div className="flex items-center justify-center h-32">
      <Result
        icon={<LockOutlined style={{ color: '#ccc' }} />}
        title="Access Denied"
        subTitle="You don't have permission to view this content"
        style={{ padding: '16px' }}
      />
    </div>
  )
}

// Lightweight version for inline permission checks
export function hasPermission(
  user: any,
  featureId: string,
  permission: 'read' | 'write' | 'delete' = 'read'
): boolean {
  if (!user) return false
  
  return user.feature_access?.some((fa: any) => {
    if (fa.feature.name !== featureId) return false
    
    switch (permission) {
      case 'read':
        return fa.can_read
      case 'write':
        return fa.can_write
      case 'delete':
        return fa.can_delete
      default:
        return false
    }
  }) || false
}

// Helper to check if user has any role
export function hasRole(user: any, roleName: string): boolean {
  if (!user) return false
  
  return user.user_roles?.some((ur: any) => ur.role.name === roleName) || false
}

// Helper to check if user has admin privileges
export function isAdmin(user: any): boolean {
  return hasRole(user, 'admin') || hasRole(user, 'super_admin')
} 